/** @jsx isolateComponent */
import { useState } from 'react';
import { css } from '@emotion/react';
import { darkenColor } from './utils/darkenColor';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import type { AnchorTarget, IconName } from '@zapier/design-system';
import {
  useClickOutside,
  Animation,
  Colors,
  Shadows,
  Typography,
  Avatar,
  Heading,
  Icon,
  Link,
  Menu,
  MenuItem,
  TagLabel,
  TooltipWrapper,
  useZdsColor,
} from '@zapier/design-system';
import { ServiceIcons } from '../ServiceIcons';
import { useResizeObserver } from './useResizeObserver';
import { ZapCardCTA } from './ZapCardCTA';

type Service = {
  name: string;
  src: string;
};

type Services = Array<Service>;

type MenuItem = {
  text: string;
  icon: IconName;
  href?: string;
  onClick?: (e: React.MouseEvent, item: MenuItem) => void;
};

type RenderMenuProps = {
  closeMenu: () => void;
};

type Layout = 'auto' | 'card' | 'line' | 'mega';

export type Props = {
  /**
   * URL pointing to the author's avatar image.
   */
  authorAvatarUrl?: string;
  /**
   * Name of the Zap's author.
   */
  authorName?: string;
  /**
   * Function to render the author, which may render above
   * the card or within it, depending on the `layout` prop.
   */
  renderAuthor?: (
    props: Props,
    computedState: ComputedState
  ) => React.ReactNode;
  /**
   * Whether clicking outside of an expanded `ZapCard` closes it.
   */
  canClickOutsideToClose?: boolean;
  /**
   * Color of the card. Typically neutral100 or white, but can be any color
   * to support platform apps' branding. **Note:** `ZapCard`
   * will only be colored when its `layout` is `card`.
   */
  color?: string;
  /**
   * Detailed content to render about the Zap, such as its steps,
   * a description of it, etc. `children` will only render if
   * the current `layout` is `line` (or `auto` and enough space
   * exists to render a `line` `layout`) and the user has expanded
   * `ZapCard` by clicking on it.
   */
  children?: React.ReactNode;
  /**
   * URL to the shared Zap / template.
   */
  href: string;
  /**
   * URL target. defaults to _self
   */
  hrefTarget?: AnchorTarget;
  /**
   * Optional click handler for when `ZapCard` is clicked.
   * Use for things like event tracking.
   */
  onClick?: (e: React.MouseEvent, computedState: ComputedState) => void;
  /**
   * Optional label to apply to the card. Use for things like "Premium"
   */
  label?: string;
  /**
   * Optional tooltip to render with the label. Use to
   * describe why the `label` has been applied.
   */
  labelTooltip?: string;
  /**
   * Function to render the label. Override to customize how the `label`
   * and `labelTooltip` props render.
   */
  renderLabel?: (props: Props, computedState: ComputedState) => React.ReactNode;
  /**
   * How `ZapCard` should render—as a squarish `card` or as a single `line`.
   * Should generally be left as default `auto` value for responsiveness.
   */
  layout?: Layout;
  /**
   * Array of items to render within the Zap's menu.
   */
  menuItems?: Array<MenuItem>;
  /**
   * Function to render the Zap's menu and its items. Override to
   * customize how the menu renders.
   */
  renderMenu?: (
    props: Props,
    computedState: ComputedState,
    renderMenuProps: RenderMenuProps
  ) => React.ReactNode;
  /**
   * Short additional information about the Zap, such as its category,
   * number of users using it, etc.
   */
  metaInfo?: React.ReactNode;
  /**
   * Function to render meta information about a Zap. Override to
   * customize how meta info renders.
   */
  renderMetaInfo?: (
    props: Props,
    computedState: ComputedState
  ) => React.ReactNode;
  /**
   * Array of services used by the Zap. Used to display service icons.
   */
  services: Services;
  /**
   * Title of the Zap.
   */
  title: string;
};

// Represents state that's computed at render time.
type ComputedColors = {
  backgroundColor: string;
  backgroundColorDark: string;
};
export type ComputedState = ComputedColors & {
  isExpanded: boolean;
  isMenuOpen: boolean;
  layout: Layout;
  width: number;
  uniqueServices: Array<string>;
};

/* Utility functions
-------------------------------------------------- */
const DEFAULT_COLOR = '#fffdf9';

const isDefaultColor = (color: Props['color']) => {
  return color === DEFAULT_COLOR;
};

const getDefaultComputedColors = (): ComputedColors => ({
  backgroundColor: DEFAULT_COLOR,
  backgroundColorDark: 'rgb(0, 0, 0, 0.25)',
});

const getComputedColors = (color: Props['color']): ComputedColors => {
  if (isDefaultColor(color) || !color) {
    return getDefaultComputedColors();
  }
  // Some of these functions may fail if bad input
  // is provided or if accessible contrast cannot
  // be achieved.
  try {
    return {
      backgroundColor: darkenColor(color, { contrast: 3 }),
      backgroundColorDark: darkenColor(color, {
        contrast: 4.5,
        minimumLightnessDistance: 10,
      }),
    };
  } catch (err) {
    return getDefaultComputedColors();
  }
};

const hasMenu = (props: Props) => {
  return (props.menuItems || []).length > 0;
};

/* Layout functions and sizes
-------------------------------------------------- */

const CARD_MAX_WIDTH = 375;
const SMALL_LINE_MAX_WIDTH = 770;
const MEDIUM_LINE_MAX_WIDTH = 615;
const ANIMATION_TRANSITION_VALUE = 'all 150ms ease-in-out';

const isCardLayout = (computedState: ComputedState) => {
  return computedState.layout === 'card';
};

const isLineLayout = (computedState: ComputedState) => {
  return computedState.layout === 'line';
};

const isMegaLayout = (computedState: ComputedState) => {
  return computedState.layout === 'mega';
};

const isCardLayoutWidth = (computedState: ComputedState) => {
  return computedState.width <= CARD_MAX_WIDTH;
};

const isWideLayoutWidth = (computedState: ComputedState) =>
  computedState.width > SMALL_LINE_MAX_WIDTH;

const isMediumLineLayoutWidth = (computedState: ComputedState) => {
  return (
    computedState.width > CARD_MAX_WIDTH &&
    computedState.width <= MEDIUM_LINE_MAX_WIDTH
  );
};

const isLargeLineLayoutWidth = (computedState: ComputedState) => {
  return computedState.width > MEDIUM_LINE_MAX_WIDTH;
};

// Elements change layout based on the size of the element.
// Some move to different locations and some hide. To prevent
// grid gaps from butting up against each other, multiple
// layouts are needed.
const computeLineLayoutGrid = (computedState: ComputedState, props: Props) => {
  if (isLargeLineLayoutWidth(computedState)) {
    return css`
      grid-template-columns: max-content max-content 1fr max-content max-content max-content;
      grid-template-areas:
        'services title title title label cta menu'
        '.        ${props.authorName
          ? 'author'
          : 'meta'}  meta  meta   meta     .   .';
    `;
  }
  return css`
    grid-template-columns: max-content 1fr max-content;
    grid-template-areas: 'services title cta';
  `;
};

const isCardOrMegaLayout = (computedState: ComputedState) => {
  return isCardLayout(computedState) || isMegaLayout(computedState);
};

// Only `card` or `mega` layouts that don't use the default color
// should be colored.
const shouldBeColored = (computedState: ComputedState) => {
  return (
    isCardOrMegaLayout(computedState) &&
    !isDefaultColor(computedState.backgroundColor)
  );
};

// Constant vars that are used throughout `Styles` that
// need to be synced.
const StyleVars = {
  cardPadding: '20px',
  cardHeaderHeight: '40px',
  cardFooterHeight: '55px',
  megaPaddingLeft: '30px',
  megaPaddingTop: '38px',
  servicesHeight: '40px',
  servicesHeightMegaCard: '60px',
};

// Reusable styles for vertically centering single-line `title`.
const centerLineCardStyles = css`
  display: flex;
  align-items: center;
  min-height: ${StyleVars.servicesHeight};
`;

export const Styles = labelStyles('ZapCard', {
  root: (props: Props, computedState: ComputedState) => [
    css`
      min-width: 270px;
    `,
    // Pad area above card to render absolutely-positioned
    // menu and / or author.
    (isCardOrMegaLayout(computedState) ||
      (isLineLayout(computedState) &&
        isMediumLineLayoutWidth(computedState))) &&
      (hasMenu(props) || props.authorName) &&
      css`
        padding-top: ${StyleVars.cardHeaderHeight};
      `,
  ],
  inner: (computedState: ComputedState) => [
    !shouldBeColored(computedState) &&
      css`
        border: 1px solid ${Colors.neutral300};
      `,
    css`
      position: relative;
      box-shadow: none;
      transition: box-shadow 150ms ${Animation.transitionTimingFunction};
      border-radius: 5px;
      background-color: ${DEFAULT_COLOR};
      // Default to undefined.
      --ZapCard-text-color: ${Colors.neutral800};

      // This is the overlay and footer background color. A pseudo
      // element is used to facilitate using CSS Grid for the disparate
      // values for the 'layout' prop.
      &::before {
        pointer-events: none;
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        transition: ${ANIMATION_TRANSITION_VALUE};
      }

      &::after {
        opacity: 0;
        content: '';
        position: absolute;
        inset: -1px;
        border: 2px solid ${Colors.blue};
        border-radius: 4px;
        transition: opacity 150ms ease-in-out;
      }
      @media (hover: hover) {
        &:hover,
        &:focus-within {
          box-shadow: ${Shadows.elevation5};
        }
      }
    `,
    isCardOrMegaLayout(computedState) &&
      css`
        --ZapCard-cta-color: ${Colors.neutral600};
        --ZapCard-cta-opacity: 0;
        @media (hover: hover) {
          &:hover,
          &:focus-within {
            --ZapCard-cta-color: ${Colors.blue};
            --ZapCard-cta-opacity: 1;
          }
        }

        &::before {
          height: ${StyleVars.cardFooterHeight};
          border-radius: 0 0 4px 4px;
          background-color: ${Colors.neutral200};
        }

        @media (hover: hover) {
          &:hover::before,
          &:focus-within::before {
            background-color: ${Colors.blue10};
          }
          &:focus-within::after {
            opacity: 1;
          }
        }
      `,
    shouldBeColored(computedState) &&
      css`
        --ZapCard-cta-color: ${Colors.neutral100};
        --ZapCard-text-color: ${Colors.neutral100};
        background-color: ${computedState.backgroundColor};

        &::before {
          background-color: ${computedState.backgroundColorDark};
        }

        @media (hover: hover) {
          &:hover,
          &:focus-within {
            --ZapCard-cta-color: ${Colors.neutral100};
          }

          &:hover,
          &:focus-within {
            box-shadow: ${Shadows.elevation10};
          }

          &:hover::before,
          &:focus-within::before {
            background-color: ${computedState.backgroundColorDark};
          }
          &:focus-within::after {
            border: 2px solid ${computedState.backgroundColorDark};
          }
        }
      `,
    isLineLayout(computedState) &&
      css`
        &::before {
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          border-radius: inherit;
        }
        @media (hover: hover) {
          &:hover::before,
          &:focus-within::before {
            background-color: ${Colors.blue10};
          }
        }
      `,
    isLineLayout(computedState) &&
      css`
        div div div span[data-color] {
          transition: ${ANIMATION_TRANSITION_VALUE};
        }

        @media (hover: hover) {
          &:hover,
          &:focus-within {
            div div div span[data-color] {
              background-color: ${Colors.neutral100};
            }
          }
          &:focus-within::after {
            opacity: 1;
          }
        }
      `,
    isLineLayout(computedState) &&
      computedState.isExpanded &&
      css`
        &::before {
          content: '';
          display: none;
        }
      `,
  ],
  layout: (computedState: ComputedState, props: Props) => [
    css`
      display: grid;
      position: relative;
    `,
    isCardOrMegaLayout(computedState) &&
      css`
        label: -card;
        min-height: 219px;
        grid-template-columns: 1fr max-content;
        grid-template-rows: ${StyleVars.servicesHeight} 1fr ${StyleVars.cardFooterHeight};
        gap: 10px;
        align-items: center;
        grid-template-areas:
          'services label'
          'title title'
          'meta cta';
        padding: ${StyleVars.cardPadding} ${StyleVars.cardPadding} 0;
      `,
    isMegaLayout(computedState) &&
      css`
        label: -mega;
        min-height: 345px;
        padding: ${StyleVars.megaPaddingTop} ${StyleVars.cardPadding} 0
          ${StyleVars.megaPaddingLeft};
        grid-template-rows: ${StyleVars.servicesHeightMegaCard} 1fr ${StyleVars.cardFooterHeight};
      `,
    isLineLayout(computedState) &&
      css`
        label: -line;
        ${computeLineLayoutGrid(computedState, props)}
        gap: 2px 20px;
        align-items: center;
        min-height: 80px;
        padding: ${StyleVars.cardPadding};
      `,
  ],
  titleArea: (computedState: ComputedState) => [
    css`
      grid-area: title;
    `,
    isLineLayout(computedState) &&
      css`
        ${centerLineCardStyles}
        align-items: inherit;
      `,
  ],
  title: (computedState: ComputedState) => [
    css`
      ${Typography.subHeader3Bold};
      color: var(--ZapCard-text-color, ${Colors.neutral800});
      // All of the properties below this are necessary
      // for line clamping to work. Even though the properties
      // have 'webkit' in them, they work cross-browser.
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 3;
      overflow: hidden;
      resize: none;
    `,
    isMegaLayout(computedState) &&
      css`
        -webkit-line-clamp: 5;
      `,

    isMegaLayout(computedState) &&
      css`
        ${isWideLayoutWidth(computedState)
          ? Typography.pageHeader7
          : Typography.pageHeader9}
      `,

    isLineLayout(computedState) &&
      css`
        ${Typography.paragraph1ShortBold}
      `,
  ],
  // Overlay link on top of content to prevent nesting
  // interactive elements.
  link: css`
    ${Typography.paragraph1ShortBold};
    display: block;
    position: absolute;
    z-index: 2;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    opacity: 0;
  `,
  ctaArea: (computedState: ComputedState) => [
    css`
      grid-area: cta;
      color: var(--ZapCard-text-color, var(--ZapCard-cta-color));
      position: relative;
      z-index: 3;
    `,
    isCardOrMegaLayout(computedState) &&
      css`
        // Entire card is clickable; let these events "bleed through."
        pointer-events: none;
        display: grid;
        grid-template-columns: max-content max-content;
        gap: 10px;
        justify-content: end;
        align-items: center;
        transition: ${ANIMATION_TRANSITION_VALUE};
      `,
    isLineLayout(computedState) &&
      css`
        ${centerLineCardStyles};
      `,
  ],
  ctaText: (computedState: ComputedState) => [
    css`
      ${Typography.smallPrint1Bold};
      color: var(--ZapCard-cta-color);
      transition: ${ANIMATION_TRANSITION_VALUE};
      text-decoration: none;
    `,
    isCardOrMegaLayout(computedState) &&
      css`
        color: var(--ZapCard-cta-color);
        transition: opacity 150ms ${Animation.transitionTimingFunction};
        opacity: var(--ZapCard-cta-opacity);
      `,

    isMegaLayout(computedState) &&
      css`
        ${Typography.paragraph3Bold};
      `,
  ],
  ctaIcon: (computedState: ComputedState) => [
    css`
      color: var(--ZapCard-cta-color);
    `,
    isLineLayout(computedState) &&
      css`
        display: none;
      `,
  ],
  menuArea: (computedState: ComputedState) => [
    css`
      position: relative;
      z-index: 4;
    `,
    // Ensure that opened menus render over top of closed menus.
    computedState.isMenuOpen &&
      css`
        z-index: 5;
      `,
    isCardOrMegaLayout(computedState) &&
      css`
        label: -card;
        display: flex;
        align-items: center;
        position: absolute;
        bottom: 100%;
        right: 0px;
        height: ${StyleVars.cardHeaderHeight};
      `,
    isLineLayout(computedState) &&
      css`
        label: -line;
        grid-area: menu;
        // Tighten gutter
        margin-left: -10px;
        ${centerLineCardStyles};
      `,
    isLineLayout(computedState) &&
      isMediumLineLayoutWidth(computedState) &&
      css`
        display: flex;
        align-items: center;
        position: absolute;
        bottom: 100%;
        right: 0px;
        height: ${StyleVars.cardHeaderHeight};
      `,
  ],
  menuToggle: css`
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${Colors.neutral600};
    &:hover,
    &:focus,
    &[aria-expanded='true'] {
      color: ${Colors.blue};
    }
  `,
  menu: (computedState: ComputedState) => [
    css`
      position: absolute;
      width: 270px;
      background: ${Colors.neutral100};
      border: 1px solid ${Colors.neutral400};
      border-radius: 3px;
      box-shadow: ${Shadows.elevation20};
      // Subtract a pixel to ensure card coverage.
      top: calc(100% - 1px);
      right: -1px;
    `,
    isLineLayout(computedState) &&
      isLargeLineLayoutWidth(computedState) &&
      css`
        top: 100%;
        right: calc(-${StyleVars.cardPadding} - 1px);
      `,
  ],
  menuInner: css`
    padding: 10px;
    color: ${Colors.neutral700};
  `,
  menuItemInner: css`
    display: grid;
    grid-template-columns: max-content 1fr;
    gap: 8px;
    align-items: center;
  `,
  menuItemText: css`
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  labelArea: (computedState: ComputedState) => [
    css`
      grid-area: label;
      position: relative;
      z-index: 3;
    `,
    isCardOrMegaLayout(computedState) &&
      css`
        text-align: right;
      `,
    isLineLayout(computedState) &&
      css`
        ${centerLineCardStyles};
      `,
    isLineLayout(computedState) &&
      isMediumLineLayoutWidth(computedState) &&
      css`
        display: none;
      `,
  ],
  servicesArea: [
    css`
      grid-area: services;
      position: relative;
      z-index: 3;
      align-self: baseline;
    `,
  ],
  metaInfoArea: (computedState: ComputedState) => [
    css`
      grid-area: meta;
      ${Typography.smallPrint3Bold};
      font-size: 14px;
      line-height: 20px;
      color: var(--ZapCard-text-color, ${Colors.neutral800});
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    `,
    isMegaLayout(computedState) &&
      css`
        font-size: 16px;
        line-height: 22px;
      `,
    isCardOrMegaLayout(computedState) &&
      css`
        pointer-events: none;
      `,
    isLineLayout(computedState) &&
      css`
        color: ${Colors.neutral600};
        line-height: 20px;
      `,
    isLineLayout(computedState) &&
      isMediumLineLayoutWidth(computedState) &&
      css`
        display: none;
      `,
  ],
  authorArea: (computedState: ComputedState) => [
    css`
      ${Typography.smallPrint1};
      gap: 10px;
    `,
    isCardOrMegaLayout(computedState) &&
      css`
        label: -card;
        display: flex;
        align-items: center;
        position: absolute;
        bottom: 100%;
        left: 0px;
        height: ${StyleVars.cardHeaderHeight};
      `,
    isLineLayout(computedState) &&
      css`
        label: -line;
        grid-area: author;
        ${centerLineCardStyles};
      `,
    isLineLayout(computedState) &&
      isLargeLineLayoutWidth(computedState) &&
      css`
        gap: 5px;
        font-size: 14px;
        min-height: auto;
        position: relative;

        // Simple but somewhat magical way to add middot.
        // Gets the job done, but might refactor if more
        // of these middot separators are needed later.
        &:not(:empty)::after {
          content: '•';
          position: absolute;
          font-size: 16px;
          right: -14px;
          bottom: 2px;
          opacity: 0.4;
        }
      `,
    isLineLayout(computedState) &&
      isMediumLineLayoutWidth(computedState) &&
      css`
        display: flex;
        align-items: center;
        position: absolute;
        bottom: 100%;
        left: 0px;
        height: ${StyleVars.cardHeaderHeight};
      `,
  ],
  authorContent: (computedState: ComputedState) => [
    css`
      display: grid;
      grid-template-columns: min-content 1fr;
      gap: inherit;
      align-items: center;
      color: ${Colors.neutral700};
    `,

    css`
      position: relative;
      z-index: ${isLineLayout(computedState) &&
      !isMediumLineLayoutWidth(computedState)
        ? 3
        : 4};
    `,
  ],
  authorByline: (computedState: ComputedState) => [
    css`
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      max-width: 22ch;
    `,
    isLineLayout(computedState) &&
      css`
        max-width: 15ch;
      `,
  ],
  authorName: css`
    color: ${Colors.neutral700};
    font-weight: 700;
  `,
  summary: css`
    display: block;
    cursor: pointer;
    position: absolute;
    z-index: 2;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    opacity: 0;
  `,
  detailsContent: css`
    position: relative;
    z-index: 3;
    padding: 0 20px 20px;
  `,
});

const defaultProps = {
  hrefTarget: '_self' as AnchorTarget,
  layout: 'auto' as Layout,
  menuItems: [],
  renderAuthor: (props: Props, computedState: ComputedState) => {
    if (!props.authorName) {
      return null;
    }
    // This is a simple implementation that doesn't worry about compound names.
    const [first, ...rest] = props.authorName.split(/\s+/);
    const last = rest.pop();
    const lastInitial = last ? `${last[0]}.` : '';
    const name = `${first} ${lastInitial}`.trim();
    const author = (
      <p aria-hidden={true} css={Styles.authorByline(computedState)}>
        by <span css={Styles.authorName}>{name}</span>
      </p>
    );

    return (
      <div
        aria-label={`by ${props.authorName}`}
        css={Styles.authorContent(computedState)}
      >
        <Avatar
          ariaHidden={true}
          isBlock={true}
          name={props.authorName}
          size={
            isLineLayout(computedState) && isLargeLineLayoutWidth(computedState)
              ? 18
              : 25
          }
          url={props.authorAvatarUrl}
        />

        <TooltipWrapper
          content={props.authorName}
          position="southwest"
          shouldCorrectPosition={false}
        >
          {() => author}
        </TooltipWrapper>
      </div>
    );
  },
  renderLabel: (props: Props, computedState: ComputedState) => {
    const color = shouldBeColored(computedState) ? 'white' : 'info';

    return (
      props.label && (
        <TooltipWrapper
          content={props.labelTooltip}
          position="southeast"
          shouldCorrectPosition={false}
        >
          {() => <TagLabel color={color}>{props.label}</TagLabel>}
        </TooltipWrapper>
      )
    );
  },
  renderMenu: (
    props: Props,
    computedState: ComputedState,
    { closeMenu }: RenderMenuProps
  ) => {
    if (!hasMenu(props)) {
      return null;
    }
    const items = (props.menuItems || []).map((item) => {
      const onClick = (e: React.MouseEvent) => {
        // Optional chaining not supported by eslint yet.
        // eslint-disable-next-line no-unused-expressions
        item.onClick && item.onClick(e, item);
        closeMenu();
      };
      const icon = item.icon && <Icon name={item.icon} size={24} />;
      return (
        <MenuItem href={item.href} key={item.text} onClick={onClick}>
          <span css={Styles.menuItemInner}>
            {icon}
            <span css={Styles.menuItemText}>{item.text}</span>
          </span>
        </MenuItem>
      );
    });
    return (
      <div css={Styles.menu(computedState)}>
        <Menu ariaLabel="Zap options">
          <div css={Styles.menuInner}>{items}</div>
        </Menu>
      </div>
    );
  },
  renderMetaInfo: (props: Props, computedState: ComputedState) => {
    if (props.metaInfo) {
      return props.metaInfo;
    }
    const truncatedServices = computedState.uniqueServices.slice(0, 3);
    const countDiff =
      computedState.uniqueServices.length - truncatedServices.length;
    return [...truncatedServices, countDiff > 0 && `${countDiff} more`]
      .filter(Boolean)
      .join(' + ');
  },
};

/**
 * Card representing a Zap, Shared Zap, Zap Template, etc.
 */
export const ZapCard = (_props: Props) => {
  const colorValue = useZdsColor(_props.color || DEFAULT_COLOR);
  const props = { ...defaultProps, ..._props, color: colorValue };

  // Menu code
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const closeMenu = () => setIsMenuOpen(false);
  const [setMenuOutsideClickNode] = useClickOutside({
    onClickOutside: closeMenu,
  });

  let menuNode: HTMLDivElement | null = null;
  const setMenuNode = (node: HTMLDivElement) => {
    menuNode = node;
    setMenuOutsideClickNode(node);
  };
  const onClick = (e: React.MouseEvent) => {
    if (!menuNode || !menuNode.contains(e.target as Node)) {
      // Optional chaining not supported by eslint yet.
      // eslint-disable-next-line no-unused-expressions, no-use-before-define
      props.onClick && props.onClick(e, computedState);
    }
  };

  // Expansion code 1
  const [isExpanded, setIsExpanded] = useState(false);
  const [setExpansionOutsideClickNode] = useClickOutside({
    onClickOutside: () => {
      if (props.canClickOutsideToClose) {
        setIsExpanded(false);
      }
    },
  });

  // Dynamic sizing
  const { ref, width } = useResizeObserver();
  const uniqueServices = [
    ...new Set(
      props.services.map((service) => {
        return service.name;
      })
    ),
  ];
  const computedState: ComputedState = {
    isExpanded,
    isMenuOpen,
    layout: props.layout as Layout,
    ...getComputedColors(colorValue),
    width,
    uniqueServices,
  };
  // Reassigned to pass `computedState` into `isCardLayoutWidth`.
  computedState.layout =
    props.layout !== 'auto'
      ? (props.layout as Layout)
      : isCardLayoutWidth(computedState)
      ? ('card' as Layout)
      : ('line' as Layout);

  // Expansion code 2
  const canExpand = props.children && isLineLayout(computedState);

  // Dynamic nodes and content
  const renderedMenu = props.renderMenu(props, computedState, { closeMenu });
  const renderedLabel = props.renderLabel(props, computedState);
  const renderedAuthor = props.renderAuthor(props, computedState);
  const renderedMetaInfo = props.renderMetaInfo(props, computedState);
  const link = (isCardLayout(computedState) || !props.children) && (
    <Link css={Styles.link} href={props.href} target={props.hrefTarget}>
      {props.title}
    </Link>
  );
  const details = canExpand && (
    <details
      css={Styles.details}
      data-testid="ZapCard__details"
      open={isExpanded}
    >
      <summary
        css={Styles.summary}
        onClick={(e) => {
          e.preventDefault();
          setIsExpanded(!isExpanded);
        }}
      >
        More details
      </summary>
      <div css={Styles.detailsContent}>{props.children}</div>
    </details>
  );

  const cta = isCardOrMegaLayout(computedState) ? (
    <ZapCardCTA
      computedState={computedState}
      ariaHidden={!!link}
      href={props.href}
      isLink={true}
      target={props.hrefTarget}
    />
  ) : isLineLayout(computedState) ? (
    <ZapCardCTA
      computedState={computedState}
      ariaHidden={!!link}
      href={props.href}
      isLink={false}
      target={props.hrefTarget}
    />
  ) : null;

  const serviceIconSize = isMegaLayout(computedState) ? 60 : 40;

  // Use eslint-disable below since accessibility is handled elsewhere.
  return (
    <article css={Styles.root(props, computedState)} ref={ref}>
      {/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-static-element-interactions */}
      <div
        css={Styles.inner(computedState)}
        data-testid="ZapCard__inner"
        onClick={onClick}
        ref={setExpansionOutsideClickNode}
      >
        <div css={Styles.layout(computedState, props)}>
          <div css={Styles.titleArea(computedState)}>
            <Heading css={Styles.title(computedState)}>{props.title}</Heading>
          </div>
          {renderedAuthor && (
            <div css={Styles.authorArea(computedState)}>{renderedAuthor}</div>
          )}
          <div css={Styles.labelArea(computedState)}>{renderedLabel}</div>
          {link}
          {cta}
          <div css={Styles.menuArea(computedState)} ref={setMenuNode}>
            {renderedMenu && (
              <button
                aria-expanded={isMenuOpen}
                aria-label="More options"
                css={Styles.menuToggle}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                type="button"
              >
                <Icon name="navMoreHoriz" size={32} />
              </button>
            )}
            {isMenuOpen && renderedMenu}
          </div>
          <div css={Styles.servicesArea}>
            <TooltipWrapper
              allowMultilineTooltip={true}
              content={uniqueServices.join(', ')}
              position="southwest"
              shouldCorrectPosition={false}
              tooltipId="tooltip-zap-card"
            >
              {() => (
                <ServiceIcons
                  ariaLabel={`Zap uses ${uniqueServices.join(', ')}`}
                  maxServices={3}
                  services={props.services}
                  size={serviceIconSize}
                  hasBorder={!shouldBeColored(computedState)}
                />
              )}
            </TooltipWrapper>
          </div>
          <div css={Styles.metaInfoArea(computedState)}>{renderedMetaInfo}</div>
        </div>
        {details}
      </div>
    </article>
  );
};
